import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Homework4Question.css';
import './ImageTransformProcessor.css';

interface RestorationImages {
  original: string | null;
  afterMedian: string | null;
  afterWiener: string | null;
  afterDeconv: string | null;
  final: string | null;
}

const Homework7Main: React.FC = () => {
  const [images, setImages] = useState<RestorationImages>({
    original: null,
    afterMedian: null,
    afterWiener: null,
    afterDeconv: null,
    final: null
  });

  const [parameters, setParameters] = useState({
    medianSize: 4,
    wienerWindow: 27,
    motionLength: 97,
    motionAngle: 315
  });

  const [processing, setProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadPresetImage = (imageNum: number) => {
    const imagePath = `/hw7_images/HW7_${imageNum}.png`;
    setImages({
      original: imagePath,
      afterMedian: null,
      afterWiener: null,
      afterDeconv: null,
      final: null
    });

    // Set image-specific parameters
    if (imageNum === 1) {
      setParameters({ medianSize: 7, wienerWindow: 22, motionLength: 97, motionAngle: 315 });
    } else if (imageNum === 2) {
      setParameters({ medianSize: 5, wienerWindow: 20, motionLength: 97, motionAngle: 315 });
    } else if (imageNum === 3) {
      setParameters({ medianSize: 3, wienerWindow: 40, motionLength: 97, motionAngle: 315 });
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImages({
          original: e.target?.result as string,
          afterMedian: null,
          afterWiener: null,
          afterDeconv: null,
          final: null
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const processImage = async () => {
    if (!images.original) {
      alert('Please load an image first!');
      return;
    }

    setProcessing(true);
    setCurrentStep('Loading image...');

    try {
      // Get base64 image data
      let imageBase64 = images.original;
      if (imageBase64.startsWith('data:')) {
        // Already has data URL prefix
      } else if (imageBase64.startsWith('/')) {
        // It's a path, need to fetch and convert
        const response = await fetch(imageBase64);
        const blob = await response.blob();
        const reader = new FileReader();
        imageBase64 = await new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
      }

      setCurrentStep('Step 1: Median filter (noise reduction)...');
      
      // Call Firebase function
      const response = await axios.post(
        'https://us-central1-image-processing-vk.cloudfunctions.net/process_homework7',
        {
          image: imageBase64,
          medianSize: parameters.medianSize,
          wienerWindow: parameters.wienerWindow,
          motionLength: parameters.motionLength,
          motionAngle: parameters.motionAngle
        },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 120000 // 2 minutes timeout for processing
        }
      );

      if (response.data.success) {
        setImages(prev => ({
          ...prev,
          afterMedian: `data:image/png;base64,${response.data.after_median}`,
          afterWiener: `data:image/png;base64,${response.data.after_wiener}`,
          afterDeconv: `data:image/png;base64,${response.data.after_deconv}`,
          final: `data:image/png;base64,${response.data.final}`
        }));
        setCurrentStep('Restoration complete!');
        console.log(`Noise variance: ${response.data.noise_variance.toFixed(6)}`);
      } else {
        throw new Error(response.data.error || 'Processing failed');
      }
    } catch (error) {
      console.error('Processing error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      alert(`Error processing image: ${errorMsg}\nPlease try another image or upload a different one.`);
      setCurrentStep(`Error: ${errorMsg}`);
    } finally {
      setProcessing(false);
      setTimeout(() => setCurrentStep(''), 3000);
    }
  };


  return (
    <div className="hw4-question-container">
      <div className="content-wrapper">
        <Link to="/homework/7" className="back-button">
          ← Back to HW7
        </Link>

        <header className="question-header">
          <h1>🖼️ Advanced Image Restoration</h1>
          <p className="subtitle">Multi-step restoration pipeline for severely degraded images</p>
        </header>

        {/* Controls */}
        <div className="controls-section">
          <h3>Load Image</h3>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
            <button onClick={() => loadPresetImage(1)} className="control-button">
              Load HW7_1
            </button>
            <button onClick={() => loadPresetImage(2)} className="control-button">
              Load HW7_2
            </button>
            <button onClick={() => loadPresetImage(3)} className="control-button">
              Load HW7_3
            </button>
            <button onClick={() => fileInputRef.current?.click()} className="control-button">
              Upload Custom Image
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </div>

          <h3>Parameters</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label>Median Filter Size: {parameters.medianSize}x{parameters.medianSize}</label>
              <input
                type="range"
                min="3"
                max="7"
                step="2"
                value={parameters.medianSize}
                onChange={(e) => setParameters({ ...parameters, medianSize: parseInt(e.target.value) })}
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label>Wiener Window: {parameters.wienerWindow}x{parameters.wienerWindow}</label>
              <input
                type="range"
                min="5"
                max="50"
                value={parameters.wienerWindow}
                onChange={(e) => setParameters({ ...parameters, wienerWindow: parseInt(e.target.value) })}
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label>Motion Length: {parameters.motionLength}</label>
              <input
                type="range"
                min="30"
                max="150"
                value={parameters.motionLength}
                onChange={(e) => setParameters({ ...parameters, motionLength: parseInt(e.target.value) })}
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label>Motion Angle: {parameters.motionAngle}°</label>
              <input
                type="range"
                min="0"
                max="360"
                value={parameters.motionAngle}
                onChange={(e) => setParameters({ ...parameters, motionAngle: parseInt(e.target.value) })}
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <button
            onClick={processImage}
            disabled={!images.original || processing}
            className="process-button"
            style={{
              background: processing ? '#6b7280' : 'linear-gradient(135deg, #10b981, #059669)',
              cursor: processing ? 'not-allowed' : 'pointer',
              padding: '15px 40px',
              fontSize: '18px',
              fontWeight: 'bold',
              border: 'none',
              borderRadius: '10px',
              color: 'white',
              width: '100%',
              maxWidth: '300px'
            }}
          >
            {processing ? `Processing... (${currentStep})` : 'Restore Image'}
          </button>
        </div>

        {/* Results */}
        <div className="results-section" style={{ marginTop: '40px' }}>
          <h3>Restoration Pipeline</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
            <div className="result-card">
              <h4>Original</h4>
              {images.original && (
                <img src={images.original} alt="Original" style={{ width: '100%', border: '2px solid #e5e7eb', borderRadius: '8px' }} />
              )}
            </div>

            {images.afterMedian && (
              <div className="result-card">
                <h4>After Median</h4>
                <img src={images.afterMedian} alt="After Median" style={{ width: '100%', border: '2px solid #10b981', borderRadius: '8px' }} />
              </div>
            )}

            {images.afterWiener && (
              <div className="result-card">
                <h4>After Wiener</h4>
                <img src={images.afterWiener} alt="After Wiener" style={{ width: '100%', border: '2px solid #3b82f6', borderRadius: '8px' }} />
              </div>
            )}

            {images.afterDeconv && (
              <div className="result-card">
                <h4>After Deblur</h4>
                <img src={images.afterDeconv} alt="After Deconv" style={{ width: '100%', border: '2px solid #f59e0b', borderRadius: '8px' }} />
              </div>
            )}

            {images.final && (
              <div className="result-card">
                <h4>Final Restored</h4>
                <img src={images.final} alt="Final" style={{ width: '100%', border: '2px solid #ef4444', borderRadius: '8px' }} />
              </div>
            )}
          </div>
        </div>

        <div className="info-box" style={{ marginTop: '40px' }}>
          <h4>About This Implementation</h4>
          <p style={{ marginTop: '10px', fontSize: '14px', color: '#6b7280' }}>
            <strong>Emir Ersel Bilgiç - 200403063</strong>
          </p>
          <p style={{ marginTop: '10px', fontSize: '14px', color: '#6b7280' }}>
            This web implementation uses Python backend with Firebase Functions for full frequency domain restoration.
            The pipeline includes: Median Filter → Wiener Filter → Motion Deconvolution (FFT-based) → Notch Filter.
          </p>
          <p style={{ marginTop: '10px', fontSize: '14px', color: '#6b7280' }}>
            <strong>Implementation:</strong> All algorithms are implemented in Python (hw7_functions.py) matching the MATLAB version (restoration_manual.m).
          </p>
        </div>
      </div>
    </div>
  );
};

export default Homework7Main;
